import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
    ArrowRight, ArrowUpRight, CheckCircle2, Clock, Headphones,
    IndianRupee, Phone, ShieldCheck,
} from "lucide-react";
import { useQuote } from "@/components/QuoteProvider";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { CONTACT, IMAGES, SEGMENTS, PCB_TYPES, PROCESS_STEPS, PILLARS } from "@/data/site";

const PILLAR_ICONS = { ShieldCheck, IndianRupee, Headphones, Clock };

const lineVariants = {
    hidden: { y: "115%" },
    visible: (i) => ({
        y: 0,
        transition: { duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
    }),
};

function Counter({ value, label, testId }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.5 });
    const [n, setN] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration: 1.4,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setN(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, value]);

    return (
        <div ref={ref} className="px-4 py-5 sm:px-6 lg:px-8" data-testid={testId}>
            <p className="text-2xl font-bold tracking-tight text-white md:text-3xl">
                {String(n).padStart(2, "0")}
            </p>
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300">
                {label}
            </p>
        </div>
    );
}

function Hero() {
    const { openQuote } = useQuote();
    const { scrollY } = useScroll();
    const yImg = useTransform(scrollY, [0, 800], [0, 130]);
    const yText = useTransform(scrollY, [0, 800], [0, 60]);

    return (
        <section className="relative overflow-hidden bg-navy-dark text-white" data-testid="home-hero">
            <motion.div style={{ y: yImg }} className="absolute inset-0" aria-hidden="true">
                <img
                    src="/images/factory-drill.jpg"
                    alt=""
                    className="h-[120%] w-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/85 to-navy-dark/40" />
            </motion.div>
            <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />

            <motion.div style={{ y: yText }} className="relative mx-auto max-w-7xl px-4 pb-16 pt-20 sm:px-6 md:pt-28 lg:px-8">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="flex items-center gap-3 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-slate-400"
                    data-testid="hero-eyebrow"
                >
                    <span className="inline-block h-2 w-2 bg-brand-red" />
                    PCB Manufacturer — Sahibabad, Ghaziabad, U.P., India
                </motion.p>

                <h1 className="mt-8 max-w-4xl text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl md:text-6xl">
                    {["Professional", "PCB Manufacturing", "Solutions."].map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="visible">
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.75 }}
                    className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg"
                    data-testid="hero-subheadline"
                >
                    Reliable PCB Manufacturing for Electronics, Industrial and Power Applications.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.9 }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    <button
                        onClick={openQuote}
                        data-testid="hero-get-quote-btn"
                        className="border border-brand-red bg-brand-red px-8 py-4 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                    >
                        Get a Quote
                    </button>
                    <button
                        onClick={openQuote}
                        data-testid="hero-send-gerber-btn"
                        className="group flex items-center gap-2 border border-navy-line bg-navy/40 px-8 py-4 text-[13px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:border-slate-400"
                    >
                        Send Gerber Data
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </button>
                </motion.div>
            </motion.div>

            <div className="relative border-t border-navy-line bg-navy/60 backdrop-blur-sm">
                <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px md:grid-cols-4" data-testid="hero-spec-strip">
                    <Counter value={12} label="Market Segments Served" testId="hero-counter-segments" />
                    <Counter value={4} label="PCB Board Classes" testId="hero-counter-types" />
                    <Counter value={6} label="Stage Production Process" testId="hero-counter-stages" />
                    <div className="flex flex-col justify-center px-4 py-5 sm:px-6 lg:px-8">
                        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300" data-testid="hero-gerber-note">
                            <span className="text-brand-red">04</span> · Gerber Data Accepted
                        </p>
                        <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">
                            Single-sided to multilayer · FR-4 & metal core · Prototype to production
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SegmentMarquee() {
    return (
        <div className="border-b border-slate-200 bg-white py-5" data-testid="segment-marquee">
            <Marquee speed={40} gradient gradientColor="#ffffff" gradientWidth={80}>
                {SEGMENTS.map((s) => (
                    <span key={s.id} className="mx-8 flex items-center gap-8 font-mono text-[12px] font-medium uppercase tracking-[0.2em] text-slate-500">
                        {s.name}
                        <span className="inline-block h-1.5 w-1.5 rotate-45 bg-brand-red" />
                    </span>
                ))}
            </Marquee>
        </div>
    );
}

function AboutPreview() {
    return (
        <section className="bg-white py-20 md:py-28" data-testid="home-about">
            <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                <FadeUp className="relative">
                    <div className="relative overflow-hidden">
                        <img src={IMAGES.about} alt="Printed circuit boards manufactured by Unimicron Technology India" className="h-[380px] w-full object-cover md:h-[460px]" />
                        <div className="absolute inset-0 border border-navy/10" />
                    </div>
                    <div className="absolute -bottom-4 -left-4 hidden h-24 w-24 border-b-2 border-l-2 border-brand-red md:block" aria-hidden="true" />
                    <p className="absolute bottom-4 left-4 bg-navy-dark px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                        PCB Manufacturing — U.P., India
                    </p>
                </FadeUp>

                <div>
                    <FadeUp>
                        <Eyebrow>About Unimicron Technology India</Eyebrow>
                        <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-navy md:text-4xl">
                            A dependable PCB manufacturing partner for your electronics
                        </h2>
                        <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                            Unimicron Technology India provides PCB manufacturing solutions for
                            electronics manufacturers and industrial customers. From single-sided
                            boards to multilayer and metal core PCBs, we manufacture to customer
                            designs with a clear focus on quality, competitive pricing, reliable
                            service and timely delivery.
                        </p>
                    </FadeUp>
                    <div className="mt-10 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2" data-testid="home-pillars">
                        {PILLARS.map((p, i) => {
                            const Icon = PILLAR_ICONS[p.icon];
                            return (
                                <FadeUp key={p.title} delay={i * 0.08} className="bg-white p-5 transition-colors duration-200 hover:bg-slate-50">
                                    <Icon className="h-5 w-5 text-brand-red" strokeWidth={1.75} />
                                    <h3 className="mt-3 text-[15px] font-semibold text-navy">{p.title}</h3>
                                    <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{p.desc}</p>
                                </FadeUp>
                            );
                        })}
                    </div>
                    <FadeUp className="mt-10">
                        <Link
                            to="/about"
                            data-testid="home-about-more-link"
                            className="group inline-flex items-center gap-2 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:text-brand-red"
                        >
                            More About Us
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}

function PCBPreview() {
    return (
        <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="home-pcb">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <FadeUp className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <Eyebrow>PCB Manufacturing</Eyebrow>
                        <h2 className="mt-5 max-w-xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            Boards manufactured to your design, layer by layer
                        </h2>
                    </div>
                    <Link
                        to="/pcb-manufacturing"
                        data-testid="home-pcb-all-link"
                        className="group inline-flex shrink-0 items-center gap-2 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:text-brand-red"
                    >
                        All PCB Types
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </FadeUp>

                <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-pcb-grid">
                    {PCB_TYPES.map((t, i) => (
                        <FadeUp key={t.id} delay={i * 0.08} className="bg-white">
                            <Link
                                to={`/pcb-manufacturing#${t.id}`}
                                data-testid={`home-pcb-card-${t.id}`}
                                className="group flex h-full flex-col p-7 transition-colors duration-200 hover:bg-navy"
                            >
                                <span className="font-mono text-[11px] tracking-[0.2em] text-brand-red">{t.num}</span>
                                <h3 className="mt-5 text-lg font-semibold text-navy transition-colors group-hover:text-white">{t.name}</h3>
                                <p className="mt-3 flex-1 text-[13px] leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">
                                    {t.desc.split(". ")[0]}.
                                </p>
                                <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400 transition-colors group-hover:text-brand-red">
                                    Details <ArrowUpRight className="h-3.5 w-3.5" />
                                </span>
                            </Link>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

function InfraPreview() {
    return (
        <section className="bg-white py-20 md:py-28" data-testid="home-infrastructure">
            <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-8">
                <div className="lg:col-span-3">
                    <FadeUp>
                        <Eyebrow>Infrastructure</Eyebrow>
                        <h2 className="mt-5 max-w-xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            A disciplined production process from Gerber to dispatch
                        </h2>
                        <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-slate-600">
                            Our manufacturing flow covers every stage of PCB production — engineering
                            review, imaging, etching, lamination, drilling, plating, finishing,
                            inspection, testing and careful packing.
                        </p>
                    </FadeUp>
                    <div className="mt-10 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2" data-testid="home-process-grid">
                        {PROCESS_STEPS.slice(0, 4).map((s, i) => (
                            <FadeUp key={s.num} delay={i * 0.06} className="bg-white p-5">
                                <span className="font-mono text-[11px] tracking-[0.2em] text-brand-red">{s.num}</span>
                                <h3 className="mt-2 text-[14.5px] font-semibold text-navy">{s.title}</h3>
                                <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-500">{s.desc}</p>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp className="mt-8">
                        <Link
                            to="/infrastructure"
                            data-testid="home-infra-link"
                            className="group inline-flex items-center gap-2 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:text-brand-red"
                        >
                            Explore Infrastructure
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                    </FadeUp>
                </div>
                <FadeUp className="relative lg:col-span-2">
                    <img
                        src={IMAGES.pcbBoard}
                        alt="PCB manufacturing at Unimicron Technology India"
                        className="h-full min-h-[380px] w-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 bg-navy-dark px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                        PCB Manufacturing
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}

function QualityStrip() {
    return (
        <section className="border-y border-slate-200 bg-slate-50 py-16 md:py-20" data-testid="home-quality">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <FadeUp className="flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
                    <div className="max-w-xl">
                        <Eyebrow>Quality</Eyebrow>
                        <h2 className="mt-4 text-2xl font-bold tracking-tight text-navy md:text-3xl">
                            Consistent manufacturing, inspected at every stage
                        </h2>
                    </div>
                    <Link
                        to="/quality"
                        data-testid="home-quality-link"
                        className="group inline-flex shrink-0 items-center gap-2 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:text-brand-red"
                    >
                        Our Quality Approach
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </FadeUp>
                <div className="mt-10 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-quality-points">
                    {[
                        "Process-controlled consistent manufacturing",
                        "Multi-stage inspection at every production stage",
                        "Built strictly to customer specifications",
                        "Reliable PCB quality, batch after batch",
                    ].map((point, i) => (
                        <FadeUp key={point} delay={i * 0.06} className="flex items-start gap-3 bg-white p-5">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                            <p className="text-[13.5px] leading-relaxed text-slate-700">{point}</p>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

function CTABand() {
    const { openQuote } = useQuote();
    return (
        <section className="relative overflow-hidden bg-navy-dark py-20 text-white md:py-24" data-testid="home-cta">
            <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
            <div className="relative mx-auto flex max-w-7xl flex-col justify-between gap-10 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
                <FadeUp>
                    <h2 className="max-w-2xl text-3xl font-bold leading-tight tracking-tight md:text-4xl">
                        Have PCB requirements to source?
                    </h2>
                    <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-300">
                        Send us your design and quantity — our team will respond with pricing and
                        delivery details. Gerber data can be attached directly to your enquiry.
                    </p>
                </FadeUp>
                <FadeUp delay={0.1} className="flex flex-col gap-4 sm:flex-row lg:flex-col lg:items-end">
                    <button
                        onClick={openQuote}
                        data-testid="home-cta-quote-btn"
                        className="border border-brand-red bg-brand-red px-8 py-4 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                    >
                        Get a Quote
                    </button>
                    <a
                        href={CONTACT.phone1Href}
                        data-testid="home-cta-phone-btn"
                        className="flex items-center gap-2 border border-navy-line px-8 py-4 font-mono text-[13px] tracking-wider text-white transition-colors hover:border-slate-400"
                    >
                        <Phone className="h-4 w-4 text-brand-red" /> {CONTACT.phone1}
                    </a>
                </FadeUp>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <SegmentMarquee />
            <AboutPreview />
            <PCBPreview />
            <InfraPreview />
            <QualityStrip />
            <CTABand />
        </>
    );
}
